import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  queries,
  mutations,
  useStoreQuery,
  type WelcomeStep,
} from "@/lib/store";

/**
 * WelcomeJourney
 *
 * The full seven-day timeline. Sisters can:
 *   - replay days they have already completed
 *   - peek the active day (highlighted)
 *   - see locked future days at a glance
 *   - bring back the dismissed Today card
 *   - (Coach) jump to any day via the demo selector
 */
export default function WelcomeJourney() {
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: onRestore/onSetDemo below WRITE with this
  // id. See the early return just below.
  const userId = me?.id ?? null;
  const isCoach = !!me && (me.role === "coach" || me.role === "admin");

  const steps = useStoreQuery(queries.welcomeSteps) ?? [];
  const progress = useStoreQuery(() => (userId ? queries.welcomeProgress(userId) : null));
  const currentDay = useStoreQuery(() => (userId ? queries.welcomeCurrentDay(userId) : null)) ?? 1;
  const completedCount = useStoreQuery(() =>
    userId ? queries.welcomeCompletedCount(userId) : 0,
  ) ?? 0;
  const isComplete = useStoreQuery(() => (userId ? queries.isWelcomeComplete(userId) : false));
  const isDismissed = useStoreQuery(() => (userId ? queries.isWelcomeDismissed(userId) : true));

  const overrideDay = progress?.demoOverrideDay ?? null;
  const overallPct = Math.round((completedCount / 7) * 100);

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see your welcome journey, sister.
        </p>
      </div>
    );
  }

  const onRestore = () => {
    mutations.restoreWelcomeAutoflow({ userId });
  };

  const onSetDemo = (day: number | null) => {
    mutations.setDemoWelcomeDay({ userId, day });
  };

  return (
    <div className="pb-24">
      <ScreenHeader
        eyebrow="Your first seven days"
        title={<>Welcome journey</>}
        subtitle={
          <>
            Seven gentle invitations from Coach Kiley. No deadlines, no streak
            penalties, no missed-day shame. Walk it once or revisit it any
            time, sister.
          </>
        }
      />

      <div className="px-5 mt-2">
        {/* Overall progress meter */}
        <GlassCard organic padding={18} className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>
              {isComplete ? "Bearing fruit" : "In the garden"}
            </p>
            <span
              className="mnf-body"
              style={{ fontSize: 12, color: "var(--mnf-tg)" }}
              data-testid="text-journey-progress"
            >
              {completedCount} of 7 days complete
            </span>
          </div>
          <div
            className="relative overflow-hidden"
            style={{
              height: 8,
              borderRadius: 999,
              background: "rgba(30,74,53,0.10)",
            }}
          >
            <div
              className="absolute inset-y-0 left-0 transition-all"
              style={{
                width: `${overallPct}%`,
                background:
                  "linear-gradient(90deg, var(--mnf-f) 0%, var(--mnf-gold) 100%)",
                borderRadius: 999,
              }}
            />
          </div>
          <p
            className="mnf-body italic mt-2"
            style={{ fontSize: 12.5, color: "var(--mnf-tg)" }}
          >
            {isComplete
              ? "You finished your first seven days, sister. The Garden is yours now."
              : `You are on day ${currentDay} of 7, walking gently.`}
          </p>

          {isDismissed && !isComplete && (
            <div className="mt-3">
              <Btn
                variant="secondary"
                size="sm"
                onClick={onRestore}
                data-testid="button-restore-autoflow"
              >
                <Icon name="arrow_back" size={14} />
                Bring it back to Today
              </Btn>
            </div>
          )}
        </GlassCard>

        {/* Coach demo selector.
            The doc comment above has always said "(Coach) jump to any day",
            but nothing checked the role, so every sister was shipped a control
            that rewrites her own welcome progress — the same class of bug as
            the tier-cycling demo button that reached production. Gated now. */}
        {isCoach && (
        <GlassCard
          organic
          padding={16}
          className="mb-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(160,120,32,0.10) 0%, rgba(245,240,232,0.7) 100%)",
          }}
        >
          <div className="flex items-start gap-3">
            <Icon name="auto_awesome" size={18} color="var(--mnf-gold)" />
            <div className="flex-1 min-w-0">
              <p
                className="mnf-eyebrow"
                style={{ color: "var(--mnf-gold)" }}
              >
                Coach preview
              </p>
              <p
                className="mnf-body mt-1"
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: "var(--mnf-tg)",
                }}
              >
                Peek any day to see what a fresh sister sees. This does not
                change her real journey, beloved.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {steps.map((s) => {
                  const selected = overrideDay === s.day;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onSetDemo(s.day)}
                      data-testid={`button-demo-day-${s.day}`}
                      className="mnf-body hover-elevate transition-all"
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        background: selected
                          ? "var(--mnf-gold)"
                          : "rgba(255,255,255,0.65)",
                        color: selected ? "#F5F0E8" : "var(--mnf-fd)",
                        border: selected
                          ? "1px solid var(--mnf-gold)"
                          : "1px solid rgba(30,74,53,0.14)",
                        cursor: "pointer",
                      }}
                    >
                      Day {s.day}
                    </button>
                  );
                })}
                {overrideDay != null && (
                  <button
                    type="button"
                    onClick={() => onSetDemo(null)}
                    data-testid="button-demo-clear"
                    className="mnf-body hover-elevate transition-all"
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      fontSize: 11.5,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      background: "transparent",
                      color: "var(--mnf-f)",
                      border: "1px solid rgba(30,74,53,0.18)",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </GlassCard>
        )}

        {/* Timeline */}
        <div className="flex flex-col gap-3">
          {steps.map((step) => (
            <JourneyDayCard
              key={step.id}
              step={step}
              userId={userId}
              currentDay={currentDay}
            />
          ))}
        </div>

        <div className="mt-6">
          <Link
            href="/"
            className="mnf-body inline-flex items-center gap-2 no-underline"
            style={{
              color: "var(--mnf-f)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
            data-testid="link-back-today"
          >
            <Icon name="arrow-left" size={14} />
            Back to Today
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Day card ----------------------------------- */

function JourneyDayCard({
  step,
  userId,
  currentDay,
}: {
  step: WelcomeStep;
  userId: string;
  currentDay: number;
}) {
  const isDone = useStoreQuery(() => queries.isWelcomeDayDone(userId, step.day)) ?? false;
  const isPrimaryDone = useStoreQuery(() =>
    queries.isWelcomeActionDone(userId, step.primaryAction.key),
  ) ?? false;
  const isSecondaryDone = useStoreQuery(() =>
    step.secondaryAction
      ? queries.isWelcomeActionDone(userId, step.secondaryAction.key)
      : false,
  ) ?? false;

  const isToday = step.day === currentDay;
  const isFuture = step.day > currentDay && !isDone;

  const palette = palettes[step.tone];

  const onPrimary = () => {
    mutations.completeWelcomeAction({
      userId,
      actionKey: step.primaryAction.key,
    });
  };
  const onSecondary = () => {
    if (!step.secondaryAction) return;
    mutations.completeWelcomeAction({
      userId,
      actionKey: step.secondaryAction.key,
    });
  };

  const statusEyebrow = isDone
    ? "Bearing fruit"
    : isToday
      ? "Today"
      : isFuture
        ? "Locked, waiting for you"
        : step.eyebrow;

  return (
    <GlassCard
      organic
      padding={0}
      className="overflow-hidden"
      data-testid={`card-journey-day-${step.day}`}
      style={{
        opacity: isFuture ? 0.78 : 1,
        boxShadow: isToday
          ? `0 0 0 2px ${palette.fg}, 0 4px 18px rgba(30,74,53,0.08)`
          : undefined,
      }}
    >
      <div
        className="relative"
        style={{
          background: palette.heroBg,
          padding: "14px 16px 12px",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 inline-flex items-center justify-center relative"
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              background: "rgba(255,255,255,0.7)",
              fontSize: 22,
              filter: isFuture ? "grayscale(0.4)" : undefined,
            }}
            aria-hidden
          >
            <span>{step.hero.primary}</span>
            {step.hero.secondary && (
              <span
                style={{
                  position: "absolute",
                  fontSize: 13,
                  transform: "translate(15px, -12px)",
                  opacity: 0.85,
                }}
              >
                {step.hero.secondary}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="mnf-body"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: palette.fg,
                }}
              >
                Day {step.day}
              </span>
              {isDone && <PillTag tone="forest">Done</PillTag>}
              {isToday && !isDone && <PillTag tone="gold">Today</PillTag>}
              {isFuture && (
                <span
                  className="inline-flex items-center gap-1 mnf-body"
                  style={{
                    fontSize: 11,
                    color: "var(--mnf-tg)",
                    fontStyle: "italic",
                  }}
                >
                  <Icon name="lock" size={12} />
                  Waiting
                </span>
              )}
            </div>
            <p
              className="mnf-eyebrow mt-1"
              style={{ color: palette.fg }}
            >
              {statusEyebrow}
            </p>
            <h3
              className="mnf-serif"
              style={{
                fontSize: 17,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                lineHeight: 1.25,
                marginTop: 2,
              }}
              data-testid={`text-journey-title-${step.day}`}
            >
              {step.title}
            </h3>
          </div>
        </div>
      </div>

      <div className="px-4 py-3.5">
        <p
          className="mnf-serif italic"
          style={{
            fontSize: 13.5,
            lineHeight: 1.5,
            color: "var(--mnf-fd)",
          }}
        >
          {`"${step.scripture}"`}
        </p>
        <p
          className="mnf-body mt-1"
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: palette.fg,
          }}
        >
          {step.scriptureRef}
        </p>

        <p
          className="mnf-body mt-3"
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--mnf-tx)",
            whiteSpace: "pre-line",
          }}
        >
          {step.body}
        </p>

        {/* Actions are tappable for today and past days, locked for future */}
        {!isFuture && (
          <div className="mt-3 flex flex-col gap-2">
            {step.primaryAction.kind === "navigate" && step.primaryAction.href ? (
              <Link
                href={step.primaryAction.href}
                onClick={onPrimary}
                className="no-underline block"
              >
                <Btn
                  variant={isPrimaryDone ? "secondary" : "primary"}
                  size="sm"
                  block
                  data-testid={`button-journey-primary-${step.day}`}
                >
                  {isPrimaryDone ? (
                    <>
                      <Icon name="check_circle" size={14} />
                      {step.primaryAction.completedLabel}
                    </>
                  ) : (
                    <>
                      <Icon name="eco" size={14} />
                      {step.primaryAction.label}
                    </>
                  )}
                </Btn>
              </Link>
            ) : (
              <Btn
                variant={isPrimaryDone ? "secondary" : "primary"}
                size="sm"
                block
                onClick={onPrimary}
                disabled={isPrimaryDone}
                data-testid={`button-journey-primary-${step.day}`}
              >
                {isPrimaryDone
                  ? step.primaryAction.completedLabel
                  : step.primaryAction.label}
              </Btn>
            )}

            {step.secondaryAction && (
              <Btn
                variant="ghost"
                size="sm"
                block
                onClick={onSecondary}
                disabled={isSecondaryDone}
                data-testid={`button-journey-secondary-${step.day}`}
              >
                {isSecondaryDone ? (
                  <>
                    <Icon name="check" size={14} />
                    {step.secondaryAction.completedLabel}
                  </>
                ) : (
                  step.secondaryAction.label
                )}
              </Btn>
            )}
          </div>
        )}

        {isFuture && (
          <div
            className="mt-3 text-center"
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: "rgba(30,74,53,0.06)",
            }}
          >
            <p
              className="mnf-body italic"
              style={{ fontSize: 12, color: "var(--mnf-tg)" }}
            >
              Opens on Day {step.day}. The garden does not rush.
            </p>
          </div>
        )}

        {isDone && (
          <div
            className="mt-3 text-center"
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: palette.softBg,
            }}
          >
            <p
              className="mnf-serif italic"
              style={{
                fontSize: 13,
                color: "var(--mnf-fd)",
                lineHeight: 1.4,
              }}
            >
              {step.completedReflection}
            </p>
          </div>
        )}
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Helpers ------------------------------------ */

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

type Tone = WelcomeStep["tone"];
const palettes: Record<
  Tone,
  { fg: string; heroBg: string; softBg: string }
> = {
  forest: {
    fg: "var(--mnf-f)",
    heroBg:
      "linear-gradient(135deg, rgba(30,74,53,0.10) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(30,74,53,0.06)",
  },
  coral: {
    fg: "var(--mnf-t)",
    heroBg:
      "linear-gradient(135deg, rgba(192,120,74,0.14) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(192,120,74,0.10)",
  },
  gold: {
    fg: "var(--mnf-gold)",
    heroBg:
      "linear-gradient(135deg, rgba(160,120,32,0.16) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(160,120,32,0.10)",
  },
  purple: {
    fg: "var(--mnf-purple)",
    heroBg:
      "linear-gradient(135deg, rgba(127,119,221,0.12) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(127,119,221,0.10)",
  },
  fiber: {
    fg: "#2C6248",
    heroBg:
      "linear-gradient(135deg, rgba(168,212,184,0.30) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(168,212,184,0.20)",
  },
};
